import {
  PERMISSION_KEYS,
  ROLE_LEVEL,
  ROLE_PERMISSIONS,
  expandPermissions,
  roleLabel,
  type PermissionKey,
} from '@supernova/shared';
import { PermissionGrid } from './permission-grid';

/**
 * Permission checklist for the Users page. What the role supplies is shown
 * ticked and locked; an owner or admin can tick extra permissions on top.
 * Anyone else sees the list read-only. A custom role supplies its own list in
 * place of the base role's defaults.
 */
export function UserPermissionsField({
  role,
  customRole,
  extras,
  onChange,
  canGrant,
}: {
  role: string;
  customRole?: { name: string; permissions: string[] } | null;
  extras: string[];
  onChange: (next: string[]) => void;
  canGrant: boolean;
}) {
  const defaults = new Set<string>(
    expandPermissions(customRole ? customRole.permissions : (ROLE_PERMISSIONS[role] ?? [])),
  );
  const name = customRole ? customRole.name : roleLabel(role);
  const hasEverything = PERMISSION_KEYS.every((k) => defaults.has(k));

  if (hasEverything) {
    return (
      <div className="rounded-lg border border-input bg-background p-3 text-sm text-muted-foreground">
        {customRole ? `${name} has every permission.` : `${name}s have every permission.`}
      </div>
    );
  }

  const change = (add: PermissionKey[], remove: PermissionKey[]) => {
    const next = new Set(extras);
    add.forEach((k) => next.add(k));
    remove.forEach((k) => next.delete(k));
    onChange(PERMISSION_KEYS.filter((k) => next.has(k) && !defaults.has(k)));
  };

  // Says plainly that ticking here never edits the role itself.
  const note = !canGrant
    ? `Boxes come from ${name}. Only Owners and Admins can add extras for one user.`
    : customRole
      ? `Locked boxes come from ${name}. Anything you tick here is extra for this user only and doesn't change the role. To change ${name} for everyone, use Roles and Permissions in User Management.`
      : `Locked boxes come from the ${name} role. Anything you tick here is extra for this user only.`;

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{note}</p>
      <PermissionGrid
        isChecked={(key) => defaults.has(key) || extras.includes(key)}
        isLocked={(key) => defaults.has(key) || !canGrant}
        lockedLabel={(key) => (defaults.has(key) ? name : null)}
        onChange={change}
        roleLevel={ROLE_LEVEL[role] ?? 0}
      />
    </div>
  );
}
